import type { ReactNode } from "react";
import { useEffect } from "react";
import ModalWrapper from "./ModalWrapper";
import { RotateDeviceIcon, SmallScreenIcon } from "@/assets/AiBotAssets";

type SmallScreenModalCta = {
  label: string;
  href?: string;
  onClick?: () => void;
};

type SmallScreenModalProps = {
  heading?: string;
  subheading?: ReactNode;
  cta?: SmallScreenModalCta | null;
  autoCloseAtWidth?: number;
  onAutoClose?: () => void;
  iconVariant?: "smallScreen" | "rotateDevice";
};

export default function SmallScreenModal({
  heading = "Screen Too Small",
  subheading = "Please access it through a device with minimum screen resolution of 1024 x 960 or higher",
  cta = {
    label: "Go to Dashboard",
    href: "/dashboard",
  },
  autoCloseAtWidth,
  onAutoClose,
  iconVariant = "smallScreen",
}: SmallScreenModalProps) {
  const ctaClasses =
    "inline-flex min-h-[38px] h-[38px] min-w-[170px] items-center justify-center rounded-[12px] bg-[#fb5f66] px-[24px] py-[12px] text-[15px] font-medium leading-none text-white transition-colors hover:bg-[#f5535b]";

  useEffect(() => {
    if (!autoCloseAtWidth || !onAutoClose) return;

    const handleViewportChange = () => {
      const isLandscape = window.innerWidth > window.innerHeight;
      if (isLandscape && window.innerWidth >= autoCloseAtWidth) {
        onAutoClose();
      }
    };

    handleViewportChange();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("orientationchange", handleViewportChange);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("orientationchange", handleViewportChange);
    };
  }, [autoCloseAtWidth, onAutoClose]);

  return (
    <ModalWrapper>
      <div className="createbot-modal small-modal sm-modal">
        <div className="relative w-full max-w-[504px] overflow-hidden rounded-[32px] bg-white px-[28px] pb-[32px] pt-[42px] shadow-[0_24px_64px_0_rgba(0,0,0,0.16)]">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-[142px] bg-[linear-gradient(180deg,rgba(251,95,102,0.28)_0%,rgba(255,255,255,0)_100%)]" />
          <div className="relative flex flex-col items-center gap-[15px] text-center">
            {iconVariant === "rotateDevice" ? (
              <RotateDeviceIcon />
            ) : (
              <SmallScreenIcon />
            )}
            <div className="flex max-w-[430px] flex-col items-center gap-[5px]">
              <h1 className="font-rubik text-[16px] font-medium leading-[110%] text-[#444444]">
                {heading}
              </h1>
              <p className="font-rubik text-[13px] leading-[140%] text-[#666666]">
                {subheading}
              </p>
            </div>
            {cta && (
              <div className="">
                {cta.href ? (
                  <a
                    className={ctaClasses}
                    href={cta.href}
                    onClick={cta.onClick}
                  >
                    {cta.label}
                  </a>
                ) : (
                  <button className={ctaClasses} onClick={cta.onClick}>
                    {cta.label}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
}
